import React from 'react';
import {
  Button,
  Card,
  CircularProgress,
  Paper,
  Typography,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';

import { ICustomTheme } from 'interfaces';

/**
 * Styles for the QuizPage. The layout mirrors `pages/ChallengeDeclaration`
 * + `components/DeclarationForm/DeclarationDescriptionBlock` so a quiz feels
 * exactly like a challenge-declaration to the end user (same banner, same
 * "last activity" banner, same description card).
 */

export const StyledCircularProgressContainer = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    padding: theme.spacing(8, 2),
  }),
);

export const StyledCircularProgress = styled(CircularProgress)(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.colors.paletteFromBO.main,
  }),
);

export const ErrorContainer = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    padding: theme.spacing(4, 2),
  }),
);

/**
 * Wrapper applied around the description block (and, in the next ticket, the
 * questions block). Strictly aligned with `StyledContainer` from
 * `components/DeclarationForm/styles.ts` — this is what gives the
 * `ChallengeDeclaration` page its 80%-wide centered column with vertical
 * breathing room around the card.
 */
export const StyledContainer = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    flex: '1 0 auto',
    margin: '0 auto',
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(6),
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
  }),
);

/**
 * Banner displayed above the description card when the user has already
 * submitted at least one entry. Strictly aligned with
 * `StyledLastDeclaration` from `DeclarationDescriptionBlock` so both pages
 * share the exact same visual treatment.
 */
export const StyledLastEntryBanner = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: 16,
      padding: theme.spacing(2, 3),
    },
    textAlign: 'center',
    '& > p': {
      margin: 0,
    },
    padding: theme.spacing(1, 2),
    fontSize: 14,
    backgroundColor: theme.colors.paletteFromBO.main,
    color: 'white',
    fontWeight: 700,
    borderRadius: theme.shape.borderRadius,
  }),
);

/**
 * Description card. Strictly aligned with `StyledCard` from
 * `DeclarationDescriptionBlock`: full width inside the page (no `maxWidth`,
 * no horizontal `margin: auto`), centered text, large desktop padding.
 */
export const StyledCard = styled(Card)(
  ({ theme }: { theme: ICustomTheme }) => ({
    boxShadow: theme.shadows[3],
    color: theme.colors.grey.dark,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(5),
    padding: `32px 48px`,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(2),
    },
  }),
);

export const StyledTitle = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    [theme.breakpoints.down('sm')]: {
      color: theme.colors.paletteFromBO.main,
      fontSize: theme.typography.h5.fontSize,
      marginBottom: theme.spacing(2),
    },
  }),
);

/**
 * Quiz description block. Aligned with `StyledTeaser` from
 * `DeclarationDescriptionBlock`: left-aligned long-form text inside a
 * centered card.
 */
export const StyledDescription = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    textAlign: 'initial',
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    color: 'inherit',
    '& p': { margin: theme.spacing(0, 0, 1) },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: theme.typography.fontWeightBold as number,
      marginBottom: theme.spacing(1),
    },
    '& ul, & ol': { paddingLeft: theme.spacing(3), marginBottom: theme.spacing(1) },
    '& a': { color: theme.colors.paletteFromBO.main },
  }),
);

export const StyledSponsor = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.palette.grey[600],
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(3),
    textAlign: 'right',
  }),
);

// ---------------------------------------------------------------------------
// Quiz form block
// ---------------------------------------------------------------------------

/** Outer paper card wrapping the whole stepper + question area */
export const StyledFormCard = styled(Paper)(
  ({ theme }: { theme: ICustomTheme }) => ({
    boxShadow: theme.shadows[3],
    padding: theme.spacing(4, 6),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  }),
);

/** Top row: "Question N / total" counter + points badge */
export const StyledStepHeader = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  }),
);

/** Progress timeline below the step counter */
export const StyledProgressTrack = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 24,
}));

const ProgressDotBase = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { active?: boolean; done?: boolean; clickable?: boolean }
>(({ active: _active, done: _done, clickable: _clickable, ...rest }, ref) =>
  React.createElement('div', { ref, ...rest }),
);

export const StyledProgressDot = styled(ProgressDotBase)(
  ({
    theme,
    active,
    done,
    clickable,
  }: {
    theme: ICustomTheme;
    active?: boolean;
    done?: boolean;
    clickable?: boolean;
  }) => ({
    width: active ? 12 : 8,
    height: active ? 12 : 8,
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor:
      done || active
        ? theme.colors.paletteFromBO.main
        : theme.palette.grey[300],
    transition: 'all 0.2s ease',
    cursor: clickable ? 'pointer' : 'default',
  }),
);

const ProgressSegmentBase = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { done?: boolean }
>(({ done: _done, ...rest }, ref) =>
  React.createElement('div', { ref, ...rest }),
);

export const StyledProgressSegment = styled(ProgressSegmentBase)(
  ({ theme, done }: { theme: ICustomTheme; done?: boolean }) => ({
    flex: 1,
    height: 2,
    backgroundColor: done
      ? theme.colors.paletteFromBO.main
      : theme.palette.grey[300],
    transition: 'background-color 0.2s ease',
  }),
);

export const StyledStepCounter = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.colors.paletteFromBO.main,
    fontWeight: theme.typography.fontWeightBold as number,
    fontSize: theme.typography.body1.fontSize,
  }),
);

/** Question title (label) */
export const StyledQuestionTitle = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    fontWeight: theme.typography.fontWeightBold as number,
    fontSize: 18,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.body1.fontSize,
    },
    color: theme.colors.paletteFromBO.main
  }),
);

/** Declarative group title */
export const StyledDeclarativeTitle = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    fontWeight: theme.typography.fontWeightBold as number,
    fontSize: 20,
    marginBottom: theme.spacing(3),
    color: theme.colors.paletteFromBO.main,
  }),
);

/** Individual question block spacing */
export const StyledQuestionBlock = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    marginBottom: theme.spacing(4),
  }),
);

export const StyledNavButton = styled(Button)(
  ({ theme }: { theme: ICustomTheme }) => ({
    borderRadius: 24,
    minWidth: 120,
    fontWeight: theme.typography.fontWeightBold as number,
    textTransform: 'none',
    padding: theme.spacing(1, 3),
  }),
);

/** Error message under a field */
export const StyledFieldError = styled(Typography)(
  ({ theme }: { theme: ICustomTheme }) => ({
    color: theme.colors.error.main,
    fontSize: theme.typography.caption.fontSize,
    marginTop: theme.spacing(0.5),
  }),
);

/** "Answer again" CTA on the post-submit success screen */
export const StyledAnswerAgainButton = styled(Button)(
  ({ theme }: { theme: ICustomTheme }) => ({
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    borderRadius: 24,
    fontWeight: theme.typography.fontWeightBold as number,
    textTransform: 'none',
    fontSize: theme.typography.caption.fontSize,
    color: theme.colors.paletteFromBO.main,
    border: `2px solid ${theme.colors.paletteFromBO.main}`,
    transition: 'all 0.25s ease',
    '&:hover': {
      backgroundColor: theme.colors.paletteFromBO.main,
      color: theme.colors.white,
      transform: 'translateY(-2px)',
      boxShadow: `0 4px 14px ${theme.colors.paletteFromBO.main}55`,
    },
  }),
);

/** Confirmation message rendered from BO HTML content */
export const StyledConfirmationMessage = styled('div')(
  ({ theme }: { theme: ICustomTheme }) => ({
    padding: '48px 0',
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    '& p': { margin: theme.spacing(0, 0, 1) },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: theme.typography.fontWeightBold as number,
      marginBottom: theme.spacing(1),
    },
    '& ul, & ol': { paddingLeft: theme.spacing(3), marginBottom: theme.spacing(1), textAlign: 'left' },
    '& a': { color: theme.colors.paletteFromBO.main },
  }),
);


